import type { Tab } from "./navigation.ts";
import { subtitleForTab } from "./navigation.ts";
// JAISIU-types-sweep: `AgentsPanel` used to be re-declared locally as a
// 7-value union, which silently drifted from the canonical list in
// `views/agents.ts` (the 8th panel, `sandbox`, landed there and this copy
// never learned about it, so `AGENT_PANEL_HINTS` failed its
// `Record<…>` completeness check). Import the one definition so the two
// cannot drift again.
import type { AgentsPanel } from "./views/agents.ts";

/** Nav tab: short label + what the section does (focus legend + title). */
export function hintForTab(tab: Tab): string {
  const sub = subtitleForTab(tab);
  return sub || `Open the ${tab} section.`;
}

const AGENT_PANEL_HINTS: Record<AgentsPanel, string> = {
  overview: "Summary: identity, model, workspace, and agents.list fields.",
  files: "Edit agent workspace bootstrap files (SOUL.md, tools, etc.).",
  tools: "Tool profile and per-tool allow/deny overrides for this agent.",
  skills: "Enable or disable skills for this agent; save config to apply.",
  channels: "Channel accounts and status linked to this agent.",
  cron: "Scheduled jobs and cron status for this agent.",
  workplan:
    "Decompose goals into context-sized tasks, publish to YouTrack, and spawn subagents per unit.",
  sandbox: "Sandbox mode, workspace access, scope, and AgentBinding routing for this agent.",
};

export function hintForAgentPanel(panel: AgentsPanel): string {
  return AGENT_PANEL_HINTS[panel];
}

export function hintSelectAgent(displayName: string, agentId: string): string {
  return `Select ${displayName} (${agentId}) — inspect workspace, tools, and files.`;
}

export function hintAgentFile(name: string, missing: boolean): string {
  return missing
    ? `Open ${name} (missing on disk — you can create it here).`
    : `Open ${name} for editing.`;
}

export const AGENT_HINTS = {
  add: "Add a new agent to agents.list (opens editor; save config to apply).",
  refreshList: "Reload the agents list from the gateway.",
  delete: "Remove this agent from agents.list (save config to apply). Workspace files are kept.",
  deleteDefaultBlocked: "Unset instance default before deleting this agent.",
  setInstanceDefault:
    "Mark as instance default (default: true on this agent). Click Save to apply.",
  setInstanceDefaultBlocked: "Add this agent to agents.list in Config before setting default.",
  registerInList: "Add this agent id to agents.list so you can edit fields here.",
  refreshChannels: "Reload channel status for this agent from the gateway.",
  refreshCron: "Reload cron jobs and scheduler status for this agent.",
  refreshFiles: "Reload the workspace file list from disk.",
  fileReset: "Discard edits and restore the file contents from disk.",
  fileSave: "Write the edited file to the agent workspace on disk.",
  toolsEnableAll: "Turn on every tool in the list for this agent.",
  toolsDisableAll: "Turn off every tool in the list for this agent.",
  toolsInherit: "Use the global tool profile instead of an agent-specific profile.",
  toolsProfile: (label: string) => `Set tool profile to “${label}” for this agent.`,
  skillsUseAll: "Clear the skills filter so all installed skills are available.",
  skillsDisableAll: "Disable every skill for this agent.",
  skillsRefresh: "Reload the skills report from the gateway.",
} as const;

export const CONFIG_HINTS = {
  reload: "Discard unsaved edits and reload config from the gateway.",
  save: "Write pending config changes to the gateway.",
} as const;

export const HELP_HINTS = {
  topic: (title: string, tagline: string) => `Help: ${title} — ${tagline}`,
  openTab: (tabLabel: string) => `Switch to ${tabLabel} and close Help.`,
  tryExample: (tryId: string) =>
    `Load guided example ${tryId} on this tab (banner + checklist; chat may prefill a prompt).`,
} as const;

export const FLEET_GUIDE_HINTS = {
  close: "Close the Fleet Control guide.",
  tabComponents: "Browse what each Fleet Control section does.",
  tabExample: "Multi-agent todo walkthrough with Open / Try shortcuts.",
  openTab: (tabLabel: string) => `Go to ${tabLabel} and close the guide.`,
} as const;

export const FLEET_TRY_HINTS = {
  dismiss: "Hide this example checklist (does not change config).",
  openGuide: "Open the Fleet Control guide (? in the top bar).",
} as const;

export const AGENTS_EDITOR_HINTS = {
  close: "Close without adding an agent.",
  cancel: "Close without adding an agent.",
  submit: "Append a new entry to agents.list (save config on Agents or Config to apply).",
} as const;

export const NAV_HINTS = {
  collapseSidebar: "Show or hide sidebar labels (icons stay visible when collapsed).",
  collapseGroup: (label: string, expanded: boolean) =>
    expanded
      ? `Collapse the “${label}” group in the sidebar.`
      : `Expand the “${label}” group in the sidebar.`,
  guide: "Open the Fleet Control guide: component help and multi-agent example.",
} as const;

export const CHAT_SESSION_TAB_HINTS = {
  closeTab: "Close this tab in the UI only (session remains on the gateway).",
  newTab: "New empty session from main (no copied transcript), then /new.",
  archiveAndNew: "Archive this tab and start a new empty chat (see Chats sidebar).",
  archiveTab: "Archive this chat — hides it from the list; transcript stays on the gateway.",
  forkTab: "Fork the current session and copy its transcript into a new tab.",
  resetTab: "Reset this tab's conversation context (/new) without forking.",
  deleteSession:
    "Delete this session and its transcript on the gateway (cannot undo). Main session cannot be deleted.",
  manageSessions: "Open chat history sidebar to pick or restore archived chats.",
  newAgent: "Open Agents tab and add a new entry to agents.list.",
  manageAgents: "Open Agents tab for the selected agent (workspace, tools, config).",
  newKind: "Fork the current session (new kind/id under this agent).",
  allKinds: "Scroll the kind row into view — all kinds from gateway sessions are listed as chips.",
  restoreArchived: "Open Chats sidebar and show archived sessions for restore.",
} as const;

export const FLEET_CHAT_SECTION_HINTS = {
  orchestration:
    "When your draft sounds like a team task: use @all, @agentId, or /best-of-n — plain text stays on one session.",
  activity:
    "Unified Activity stream — Thinking, Exec, Todo, and Health in one reverse-chronological feed.",
  subagents:
    "Parallel subagent sessions — each opens in its own tab with full transcript when spawned.",
  fleetSessions:
    "Active session, switch main/fork/subagent threads, grouped by agent. Edit agent from Agent activity.",
  sessionInspector:
    "Delegation graph, snapshot file diffs (write/edit/manual), and session background (environment, logs, gateway events).",
  environment:
    "Per-session Docker sandbox or host runtime — container status, workspace path, recreate for a clean MVP.",
  roster:
    "Fleet map: drag nodes (layout saved locally), Reset layout in toolbar; glossary in Help → How it works.",
  fleetChecklist: "Readiness checks for multi-agent setup (models, routes, defaults).",
  projects:
    "Per-agent code projects: each has a chat session and file root under .openclaw/fleet-projects/.",
  workspace:
    "Files written by tools in this session only (not bootstrap markdown or other sessions).",
  setup: "Shortcuts to gateway, agents, channels, config, and help tabs.",
} as const;

/** Fleet Control main tab bar (below session chrome). */
export const FLEET_CHAT_HISTORY_HINTS = {
  newChat: "Start a new empty chat session (fork from main, then /new).",
  archiveAndNew:
    "Archive this chat in the sidebar (keeps transcript on gateway) and open a fresh session.",
  archiveAndNewMain: "Main session cannot be archived — starts a new forked chat only.",
  archive: "Hide this chat from the list without deleting its transcript.",
  restore: "Show this chat in the main history list again.",
  refresh: "Reload chat list from the gateway (titles and previews).",
  collapse: "Collapse chat history sidebar.",
  expand: "Expand chat history sidebar (Cursor-style past chats).",
  rename: "Rename this chat. Title-only update; transcript and session id stay the same.",
} as const;

export const FLEET_CHAT_EDIT_HINTS = {
  editingPriorMessage:
    "You edited an earlier user message. Sending will ask to reset the session from that point (removes later messages).",
  cancelEdit: "Discard this edit and leave the transcript unchanged.",
} as const;

export const FLEET_CHAT_FILE_DIFF_HINTS = {
  runActivityStrip:
    "While a run is active, shows current tool activity and recent file edits with mini diff previews (clears when the run finishes).",
  fileRowClick: "Open full diff or file content for this change.",
  diffPanelClose: "Close the file diff panel.",
  activityPanel: "Thinking, tool exploration, and markdown todos extracted from the run.",
  fileContext: "Type @file: in the composer to attach workspace file context.",
  folderContext: "Type @folder: or @dir: in the composer to attach entire folder as context.",
  todoToggle: "Click the status icon to cycle pending → in progress → completed.",
  checkpointRestore: "Restore workspace files to the state before this checkpoint batch.",
} as const;

export const CHAT_ASSIST_DISPLAY_HINTS = {
  off: "Hide assistant thinking and tool output (click to show full transcript).",
  on: "Show full assistant, tool, and subagent output (click for distraction-free mode).",
  minimal: "Distraction-free: your messages and completion only (click to hide assistant output).",
} as const;

export const FLEET_CHAT_FOCUS_HINTS = {
  sessionChromeCollapse:
    "Hide session path rows and open tabs so the chat transcript uses more vertical space.",
  sessionChromeExpand:
    "Show session path navigator and open tabs (grows down into the message area).",
  sessionNavigatorScrollableOn:
    "Keep session navigator in the Chat tab; it grows down so path rows and tabs stay visible.",
  sessionNavigatorScrollableOff:
    "Classic layout: session navigator above all tabs with the previous height behavior.",
  composerToolbarCollapse: "Hide target/model toolbar for a taller message area.",
  composerToolbarExpand: "Show target and model toolbar above the message box.",
} as const;

export const FLEET_CHAT_MAIN_TAB_HINTS: Record<string, string> = {
  chat: "Messages, queue, and composer for the active session tab.",
  subagents: "Thinking, tools, todos, and parallel subagent runs for this chat.",
  projects: "Code projects created from this chat session.",
  files: "Directory tree per project linked to this chat.",
  fleetMap: "Delegation graph: which subagent is doing what; edit routes or agent model.",
};

export const FLEET_SETUP_HINTS = {
  toggle: "Show or hide platform settings shortcuts.",
  gateway: "Connect gateway URL and token on Overview.",
  agents: "Configure agents, workspaces, and tools.",
  sessions: "Admin list: patch labels, delete sessions; open any session in Fleet Control.",
  skills: "Enable skills and API keys for agents.",
  channels: "Set up Telegram, Slack, and other channels.",
  config: "Edit openclaw.json and gateway settings.",
  help: "Open Help articles for Fleet Control.",
  guide: "Open the Fleet Control walkthrough (? in the top bar).",
} as const;

export const FLEET_COMPOSER_MODE_HINTS = {
  cycle: "Shift+Tab in the message box cycles Agent → Ask → Plan → Debug → Multitask.",
  agent:
    "Default: your message stays on this session. Turn on options below only for workers or projects.",
  ask: "Read-only Q&A on this session — no file edits or shell (gateway-enforced).",
  plan: "Research and plan on this session; use Implement when you are ready to code.",
  debug: "Hypothesis-driven debugging with runtime evidence on this session.",
  multitask: "Fans out with @all to every agent — parallel workers, not a single-thread chat.",
} as const;

export const LOCAL_LLM_HINTS = {
  refresh: "Probe Ollama (/api/tags, /api/ps) and append activity to the console.",
  autoRefresh: "Refresh every 10 seconds while this tab is open.",
  gatewayLogs: "Append gateway file log lines matching “ollama” (not Ollama daemon stdout).",
  clearConsole: "Clear the console buffer (next refresh adds new lines).",
  modelPicker: "Focus activity on one installed model, or overview for all.",
} as const;

export const FLEET_SEND_OPTIONS_HINTS = {
  orchestrate:
    "Optional: prefix with @all and spawn a worker per agent (Subagent activity + Fleet map).",
  createNewProject:
    "Optional: create a new project (forked session + file root), then send your message there.",
  selfImprovement:
    "Optional: one YouTrack task (JAISIU) + code-fix worker + feature-ship. Needs YOUTRACK_URL and YOUTRACK_TOKEN on the gateway host.",
  useProject: "Optional: send into an existing project session linked to this chat.",
  groupIntro:
    "Plain messages talk to the active session. Enable an option only when you want workers or a project.",
} as const;

export const MULTI_AGENT_HINTS = {
  useAll: "Rewrite your draft to @all so every configured agent gets the task.",
  showSyntax: "Scroll to orchestration tips in the footer.",
  dismiss: "Hide this suggestion for now.",
} as const;

export const PLAN_IMPLEMENT_HINTS = {
  implement:
    "Switch to Agent mode and send an implementation request for the plan above (rewinds nothing).",
  dismiss: "Hide this prompt until the next plan reply in this session.",
} as const;

export const FLEET_QUESTION_HINTS = {
  continue: "Submit your choices and unblock the agent run.",
} as const;

export const FLEET_TODOS_HINTS = {
  toggle: "Expand or collapse the implementation checklist above the composer.",
} as const;

export const SUBAGENT_HINTS = {
  openSessionTab: (label: string) =>
    `Open a dedicated tab for “${label}” and show where you are in the thread.`,
} as const;
